import type { IconName } from "@bundle/tokens";
import { usePrimitiveT } from "../i18n/I18nProvider.tsx";
import { Button } from "./Button.tsx";
import { Icon } from "./Icon.tsx";
import { IconButton } from "./IconButton.tsx";
import { cx } from "./tokens.ts";

export type BannerAction = { label: string; onPress: () => void; testID: string; icon?: IconName };

export type BannerTone = "info" | "success" | "warning" | "danger" | "neutral" | "external";

export type BannerProps = {
  message: string;
  /** Bold first line. */
  title?: string;
  /** Colours: status.<tone>.bg / .fg / .border; neutral = bg.surface; external = external.bg / .fg (default info). */
  tone?: BannerTone;
  /** Leading glyph (default per tone: alert, warning triangle, check circle, external). */
  icon?: IconName;
  /** One action on the trailing side ("Reload", "Review"); `variant: "primary"` when it is the banner's purpose (a download). */
  action?: BannerAction & { variant?: "secondary" | "primary" };
  /** A second, always-secondary action before `action` ("Open in app"). */
  secondaryAction?: BannerAction;
  /** Present = a close button (`<testID>-dismiss`). */
  onDismiss?: () => void;
  /** Name of the close button (default "Dismiss"). */
  dismissLabel?: string;
  /**
   * "inline" (default): a rounded, outlined box inside content. "edge": a full-width strip under a header (no radius,
   * bottom border only): the external-organization notice, offline.
   */
  placement?: "inline" | "edge";
  /** The title as a bold lead-in on the message's line ("**Bundle is in beta.** Tell us…") instead of a line above it. */
  inlineTitle?: boolean;
  testID: string;
};

const defaultIcon: Record<BannerTone, IconName> = {
  info: "alert",
  neutral: "alert",
  danger: "alert",
  warning: "warning",
  success: "checkCircle",
  external: "external",
};

/** A persistent notice about the current screen. Danger and warning are announced (`role="alert"`), others politely. */
export function Banner({ message, title, tone = "info", icon, action, secondaryAction, onDismiss, dismissLabel, placement = "inline", inlineTitle = false, testID }: BannerProps) {
  const t = usePrimitiveT();
  return (
    <div className={cx("b-banner", `b-banner-${tone}`, `b-banner-${placement}`)} role={tone === "danger" || tone === "warning" ? "alert" : "status"} data-testid={testID}>
      <span className="b-banner-icon">
        <Icon name={icon ?? defaultIcon[tone]} size="icon.sm" />
      </span>
      {inlineTitle && title !== undefined ? (
        <span className="b-banner-text">
          <span className="b-banner-message">
            <span className="b-banner-lead">{title}</span> {message}
          </span>
        </span>
      ) : (
        <span className="b-banner-text">
          {title === undefined ? null : <span className="b-banner-title">{title}</span>}
          <span className="b-banner-message">{message}</span>
        </span>
      )}
      {secondaryAction === undefined ? null : (
        <Button label={secondaryAction.label} icon={secondaryAction.icon} onPress={secondaryAction.onPress} variant="secondary" size="sm" testID={secondaryAction.testID} />
      )}
      {action === undefined ? null : (
        <Button label={action.label} icon={action.icon} onPress={action.onPress} variant={action.variant ?? "secondary"} size="sm" testID={action.testID} />
      )}
      {onDismiss === undefined ? null : (
        <IconButton icon="close" size="sm" accessibilityLabel={dismissLabel ?? t("common.dismiss")} onPress={onDismiss} testID={`${testID}-dismiss`} />
      )}
    </div>
  );
}
